import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  Autocomplete,
  TextField,
  Avatar,
} from '@mui/material'
import { PersonAdd as PersonAddIcon } from '@mui/icons-material'
import { userGroupService, UserGroup, AddMemberRequest } from '../../services/userGroupService'
import { UserService } from '../../services/userService'
import { User } from '../../types/user'

interface AddMemberModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  userGroup: UserGroup
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({ open, onClose, onSuccess, userGroup }) => {
  const [availableUsers, setAvailableUsers] = useState<User[]>([])
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 利用可能なユーザー一覧を取得（グループメンバー以外）
  const fetchAvailableUsers = useCallback(async () => {
    try {
      setLoadingUsers(true)
      const response = await UserService.getUsers({ limit: 100 }) // 最大100名取得

      // 現在のグループメンバーを除外
      const memberIds = new Set(userGroup.members.map((m) => m.userId))
      const available = response.users.filter((user) => user.isActive && !memberIds.has(user.id))

      setAvailableUsers(available)
    } catch (err) {
      setError('ユーザー一覧の取得に失敗しました')
    } finally {
      setLoadingUsers(false)
    }
  }, [userGroup.members])

  useEffect(() => {
    if (open) {
      fetchAvailableUsers()
      setSelectedUser(null)
      setError(null)
    }
  }, [open, userGroup.members, fetchAvailableUsers])

  const handleSubmit = async () => {
    if (!selectedUser) {
      setError('ユーザーを選択してください')
      return
    }

    try {
      setLoading(true)
      setError(null)

      const requestData: AddMemberRequest = {
        userId: selectedUser.id,
        role: 'MEMBER',
      }

      await userGroupService.addMemberToGroup(userGroup.id, requestData)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'メンバーの追加に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    setSelectedUser(null)
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon />
          メンバー追加 - {userGroup.name}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            グループに新しいメンバーを追加します。追加されたメンバーは、このグループに割り当てられた教育プログラムを受講できるようになります。
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* ユーザー選択 */}
          <FormControl fullWidth margin="normal">
            <Autocomplete
              options={availableUsers}
              value={selectedUser}
              onChange={(_, newValue) => {
                setSelectedUser(newValue)
                if (error) setError(null)
              }}
              loading={loadingUsers}
              disabled={loading}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <Avatar sx={{ width: 32, height: 32 }}>{option.name.charAt(0)}</Avatar>
                  <Box>
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                      {option.department && ` (${option.department})`}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="追加するユーザー"
                  placeholder="名前またはメールアドレスで検索"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={
                loadingUsers
                  ? '読み込み中...'
                  : availableUsers.length === 0
                    ? '追加可能なユーザーがいません'
                    : '該当するユーザーが見つかりません'
              }
            />
          </FormControl>

          {/* 選択されたユーザー情報 */}
          {selectedUser && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                選択されたユーザー
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar>{selectedUser.name.charAt(0)}</Avatar>
                <Box>
                  <Typography variant="body2">{selectedUser.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedUser.email}
                    {selectedUser.department && ` | ${selectedUser.department}`}
                    {selectedUser.position && ` | ${selectedUser.position}`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* 現在のメンバー数表示 */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
            <Typography variant="body2" color="secondary.main">
              現在のメンバー数: {userGroup.members.length}名
              {availableUsers.length > 0 && <> | 追加可能: {availableUsers.length}名</>}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedUser || loadingUsers}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? '追加中...' : 'メンバー追加'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddMemberModal
